import React, { useState, useCallback } from 'react';
import { Table, Pagination, Card } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';
import {
  getTableHeaderClass,
  getModernDataTableClass,
  getModernCardHeaderClass,
  getModernCardBodyClass,
  getModernCardFooterClass,
  getModernTableClass,
  getModernDataTableMetaClass,
} from 'utils/tableTheme';

const Results = ({ user, data, currentTheme, onRemoveButton, onViewButton }) => {
  const history = useHistory();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(40);
  const [orderBy, setOrderBy] = useState(''); // Nova state para coluna de ordenação
  const [orderDirection, setOrderDirection] = useState('asc'); // Nova state para direção da ordenação

  const handleChangePage = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0); // Reset to first page ao mudar linhas por página
  }, []);

  // Função para manipular o clique no cabeçalho e ordenar
  const handleSort = (column) => {
    if (orderBy === column) {
      // Inverte a direção se a coluna já está selecionada
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Define uma nova coluna e inicia com ascendente
      setOrderBy(column);
      setOrderDirection('asc');
    }
    // Resetar para primeira página quando mudar ordenação
    setPage(0);
  };

  // Função para ordenar os dados com base na coluna e direção
  const sortData = (dataToSort) => {
    if (!orderBy) return dataToSort;

    return [...dataToSort].sort((a, b) => {
      let valueA, valueB;

      // Definir valores para comparação com base na coluna
      switch (orderBy) {
        case 'objectKey':
          valueA = a?.objectKey || '';
          valueB = b?.objectKey || '';
          break;
        case 'bucketKey':
          valueA = a?.bucketKey || '';
          valueB = b?.bucketKey || '';
          break;
        case 'size':
          valueA = a?.size || 0;
          valueB = b?.size || 0;
          break;
        case 'sha1':
          valueA = a?.sha1 || '';
          valueB = b?.sha1 || '';
          break;
        default:
          valueA = '';
          valueB = '';
      }

      // Comparação dos valores considerando a direção
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return orderDirection === 'asc' ? valueA - valueB : valueB - valueA;
      } else {
        const comparison = String(valueA).localeCompare(String(valueB));
        return orderDirection === 'asc' ? comparison : -comparison;
      }
    });
  };

  // Renderizar ícone de ordenação usando react-icons
  const renderSortIcon = (column) => {
    if (orderBy !== column) {
      return <FaSort style={{ marginLeft: 5, opacity: 0.5 }} />;
    }
    return orderDirection === 'asc'
      ? <FaSortUp style={{ marginLeft: 5, opacity: 1 }} />
      : <FaSortDown style={{ marginLeft: 5, opacity: 1 }} />;
  };

  // Aplicar ordenação antes da paginação
  const sortedData = sortData(data);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const paginatedData = sortedData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const getPaginationRange = () => {
    const start = Math.max(page - 2, 0);
    const end = Math.min(page + 2, totalPages - 1);
    let range = [];

    for (let i = start; i <= end; i++) {
      range.push(i);
    }
    return range;
  };

  const handleRowClick = (objectId) => {
    // Encontrar o objeto completo com base no objectId
    const modelData = data.find(item => item.objectId === objectId);
    
    // Navegar para visualização do modelo 3D passando os dados completos
    history.push({
      pathname: `/models-3d/viewer/${encodeURIComponent(objectId)}`,
      state: {
        modelData: modelData
      }
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div>
      <p className={getModernDataTableMetaClass()}>
        {data.length} registros encontrados. Página {page + 1} de {totalPages}
      </p>
      <Card
        className={getModernDataTableClass(currentTheme)}
      >
        <Card.Header
          className={getModernCardHeaderClass(currentTheme)}
        >
          Modelos 3D
        </Card.Header>
        <Card.Body
          className={getModernCardBodyClass(currentTheme)}
        >
          <Table
            style={{ fontSize: "0.9rem" }}
            hover
            responsive
            size="sm"
            className={getModernTableClass(currentTheme)}
          >
            <thead>
              <tr className={getTableHeaderClass(currentTheme)}>
                <th
                  className="text-center col-sm-3"
                  onClick={() => handleSort('objectKey')}
                  style={{ cursor: 'pointer' }}
                >
                  ARQUIVO {renderSortIcon('objectKey')}
                </th>
                <th
                  className="text-center"
                  onClick={() => handleSort('bucketKey')}
                  style={{ cursor: 'pointer' }}
                >
                  BUCKET {renderSortIcon('bucketKey')}
                </th>
                <th
                  className="text-center"
                  onClick={() => handleSort('size')}
                  style={{ cursor: 'pointer' }}
                >
                  TAMANHO {renderSortIcon('size')}
                </th>
                <th
                  className="text-center"
                  onClick={() => handleSort('sha1')}
                  style={{ cursor: 'pointer' }}
                >
                  SHA1 {renderSortIcon('sha1')}
                </th>
                
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item) => (
                <tr
                  key={item.objectId}
                  className="clickable-row"
                  onClick={() => user?.permission?.cooperatives?.read && handleRowClick(item.objectId)}
                  style={{ cursor: user?.permission?.cooperatives?.read ? 'pointer' : 'default' }}
                >
                  <td className="pt-2">{item.objectKey || '--'}</td>
                  <td className="text-center pt-2">{item.bucketKey || '--'}</td>
                  <td className="text-center pt-2">{formatFileSize(item.size) || '--'}</td>
                  <td className="text-center pt-2">
                    {item.sha1 
                      ? item.sha1.length > 12 
                        ? `${item.sha1.slice(0, 12)}...`
                        : item.sha1
                      : '--'}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
        <Card.Footer
          className={getModernCardFooterClass(currentTheme)}
        >
          <div>
            <label>
              Linhas por página:{' '}
              <select className="modern-data-table__rows-select"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}>
                {[20, 40, 60].map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <Pagination className="custom-pagination" variant="dark">
            <Pagination.Prev
              onClick={() => handleChangePage(page - 1)}
              disabled={page === 0}
              aria-label="Página anterior"
            />
            {getPaginationRange().map((index) => (
              <Pagination.Item
                key={index}
                active={index === page}
                onClick={() => handleChangePage(index)}
              >
                {index + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => handleChangePage(page + 1)}
              disabled={page >= totalPages - 1}
              aria-label="Próxima página"
            />
          </Pagination>
        </Card.Footer>
      </Card>
    </div>
  );
};

export default Results;
